import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { Task } from '../pages/TodoPage';

export interface TaskFormUser {
  name: string;
  email: string;
}

export interface TaskFormCompany {
  companyId: number;
  name: string;
}

export interface TaskFormUsergroup {
  name: string;
  usergroupId: number;
  users: TaskFormUser[];
  companyId: number;
}

export interface UseTaskFormResult {
  task: Task | undefined;
  required: Record<string, boolean>;
  companiesList: TaskFormCompany[];
  usergroupsList: TaskFormUsergroup[];
  filteredUserList: TaskFormUser[];
  hideCompany: boolean;
  renderValueCompany: string;
  renderValueUsergroup: string;
  renderValueAssign: string;
  handleChange: <Key extends keyof Task>(prop: Key, value: Task[Key]) => void;
  handleChangeAssign: (email: string) => void;
  handleChangeCompany: (companyId: number) => void;
  handleChangeUsergroup: (usergroupId: number) => void;
  reset: (initialTask?: Task) => void;
  submit: () => Promise<Task | null>;
}

export const useTaskForm = (initialTask?: Task): UseTaskFormResult => {
  const [task, setTask] = useState<Task | undefined>(initialTask);
  const [required, setRequired] = useState<Record<string, boolean>>({});
  const [usergroupsListDB, setUsergroupsListDB] = useState<TaskFormUsergroup[]>(
    [],
  );
  const [usergroupsList, setUsergroupsList] = useState<TaskFormUsergroup[]>([]);
  const [companiesList, setCompaniesList] = useState<TaskFormCompany[]>([]);
  const [hideCompany, setHideCompany] = useState<boolean>(false);
  const [filteredUserList, setFilteredUserList] = useState<TaskFormUser[]>([]);
  const [renderValueCompany, setRenderValueCompany] = useState<string>('');
  const [renderValueAssign, setRenderValueAssign] = useState<string>('');
  const [renderValueUsergroup, setRenderValueUsergroup] = useState<string>('');

  useEffect(() => {
    api
      .post(`/usergroups/getByCompanyUser`)
      .then((response: any) => {
        if (response.data) {
          const auxListCompanies = response.data.map(
            (x: any): TaskFormCompany => {
              return { companyId: x.companyId, name: x.companyName ?? x.name };
            },
          );
          const arrayCompanies: TaskFormCompany[] = [
            ...new Map<string, TaskFormCompany>(
              auxListCompanies.map((company: any) => [
                company.companyId,
                company,
              ]),
            ).values(),
          ];

          setCompaniesList(arrayCompanies);
          if (arrayCompanies.length === 1) {
            setTask((prev: any) => {
              return { ...prev, companyId: arrayCompanies[0].companyId };
            });

            setHideCompany(true);
          } else if (arrayCompanies.length > 1) {
            setHideCompany(false);
          } else {
            console.error('The systems can not find any company');
          }

          const userGroupsFromDb = response.data.map((x: any) => {
            return {
              name: x.name,
              usergroupId: x.userGroupId,
              users: x.users,
              companyId: x.companyId,
            };
          });

          setUsergroupsListDB(userGroupsFromDb);
          setUsergroupsList(userGroupsFromDb);
        }
      })
      .catch((error: any) => {
        console.error(error.response.data.message);
      });
  }, []);

  useEffect(() => {
    if (task?.usergroupId && task?.guests && usergroupsListDB) {
      const { usergroupId } = task;
      const usersFromSelectedUsergroups = usergroupsListDB
        .filter((x: any) => usergroupId === x.usergroupId)
        .map((y: any) => y.users)
        .flat();
      const filteredGuests = task?.guests.map((x: any) => {
        return { username: x.name, email: x.email };
      });
      const result = Array.from(
        new Set([...filteredGuests, ...usersFromSelectedUsergroups]),
      );
      const filtered = result.map(x => {
        return { name: x.username, email: x.email };
      });
      setFilteredUserList(filtered);
      if (task.assign) {
        setRenderValueAssign(task.assign.name);
      }
    }
  }, [task, usergroupsListDB]);

  const handleChange = useCallback(
    <Key extends keyof Task>(prop: Key, value: Task[Key]) => {
      const taskCopy: any = { ...task, guests: task?.guests ?? [] };

      if (prop === 'companyId') {
        taskCopy.assign = { name: '', email: '' };
        taskCopy.usergroupId = 0;

        setUsergroupsList(
          usergroupsListDB.filter(usergroup => usergroup.companyId === value),
        );
      }

      if (prop === 'assign') {
        setRenderValueAssign((value as any).name);
      }

      if (prop === 'usergroupId') {
        if ((value as any) > 0) {
          const usersFromSelectedUsergroups = usergroupsListDB
            .filter((x: any) => value === x.usergroupId)
            .map((y: any) => y.users)
            .flat();
          const filteredGuests =
            task?.guests.map((x: any) => {
              return { username: x.name, email: x.email };
            }) ?? [];
          const result = Array.from(
            new Set([...filteredGuests, ...usersFromSelectedUsergroups]),
          );
          const filtered = result.map(x => {
            return { name: x.username, email: x.email };
          });
          setFilteredUserList(filtered);

          if (!filtered.find(x => x.email === taskCopy?.assign.email)) {
            taskCopy.assign = { name: '', email: '' };
            setRenderValueAssign('');
          }
        } else {
          taskCopy.assign = { name: '', email: '' };
          setRenderValueAssign('');
        }
      }

      taskCopy[prop] = value;
      setTask(taskCopy);
    },
    [task, usergroupsListDB],
  );

  useEffect(() => {
    if (task?.companyId && companiesList.length > 0) {
      setRenderValueCompany(
        companiesList.find(x => x.companyId === task?.companyId)?.name ?? '',
      );
    } else {
      setRenderValueCompany('');
      if (companiesList.length === 1) {
        setHideCompany(true);
        handleChange('companyId', companiesList[0].companyId);
      }
    }
  }, [companiesList, handleChange, task?.companyId]);

  useEffect(() => {
    if (task?.usergroupId && usergroupsList.length > 0) {
      setRenderValueUsergroup(
        usergroupsList.find(x => x.usergroupId === task.usergroupId)?.name ??
          '',
      );
    } else {
      setRenderValueUsergroup('');
    }
  }, [usergroupsList, handleChange, task?.usergroupId]);

  const handleChangeAssign = useCallback(
    (email: string) => {
      handleChange('assign', {
        name: filteredUserList.find(x => x.email === email)?.name ?? '',
        email,
      } as any);
    },
    [filteredUserList, handleChange],
  );

  const handleChangeCompany = useCallback(
    (companyId: number) => {
      handleChange('companyId', companyId as any);
    },
    [handleChange],
  );

  const handleChangeUsergroup = useCallback(
    (usergroupId: number) => {
      handleChange('usergroupId', usergroupId as any);
    },
    [handleChange],
  );

  const reset = useCallback((next?: Task) => {
    setRequired({});
    setTask(
      next ??
        ({
          effort: 'Sehr Gering',
          impact: 'Sehr Gering',
          status: 'Nicht Begonnen',
        } as Task),
    );
    setRenderValueAssign('');
    setHideCompany(false);
  }, []);

  const submit = useCallback((): Promise<Task | null> => {
    let validation = {};
    if (!task?.shortTitle) {
      validation = { shortTitle: true };
    }

    if (!task?.description) {
      validation = { ...validation, description: true };
    }

    if (!task?.companyId) {
      validation = { ...validation, companyId: true };
    }

    if (!task?.usergroupId || task?.usergroupId === 0) {
      validation = { ...validation, userGroups: true };
    }

    if (!task?.assign?.email) {
      validation = { ...validation, assign: true };
    }

    if (!task?.effort) {
      validation = { ...validation, effort: true };
    }

    if (!task?.impact) {
      validation = { ...validation, impact: true };
    }

    if (!task?.status) {
      validation = { ...validation, status: true };
    }

    setRequired(validation);
    if (Object.keys(validation).length > 0) {
      console.error('Validation errors:', validation);
      return Promise.resolve(null);
    }

    const newTask = {
      taskId: task?.taskId,
      shortTitle: task?.shortTitle,
      description: task?.description,
      effort: task?.effort,
      impact: task?.impact,
      due: task?.due,
      usergroupId: task?.usergroupId,
      status: task?.status,
      goal: task?.goal,
      assign: task?.assign,
      guests: task?.guests?.map((x: any) => {
        return { name: x.name, email: x.email, lastName: x.lastName };
      }),
      companyId: task?.companyId,
    };

    const request = task?.taskId
      ? api.put('/tasks', newTask)
      : api.post('/tasks', newTask);

    return request
      .then(response => (response.status === 200 ? response.data : null))
      .catch(error => {
        console.error(error);
        return null;
      });
  }, [task]);

  return {
    task,
    required,
    companiesList,
    usergroupsList,
    filteredUserList,
    hideCompany,
    renderValueCompany,
    renderValueUsergroup,
    renderValueAssign,
    handleChange,
    handleChangeAssign,
    handleChangeCompany,
    handleChangeUsergroup,
    reset,
    submit,
  };
};
